"use client";

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { votePoll, removePollVote } from "@/lib/api/posts";
import { Facepile } from "./Facepile";
import { Button } from "./ui/Button";
import { formatShortDateTime, THETA_TIME_ZONE } from "@/lib/dates";
import type { Poll } from "@theta/api-client";

/** Radio dot for single-choice polls, checkbox for multiple-choice ones. */
function OptionMarker({
  multiple,
  selected,
}: {
  multiple: boolean;
  selected: boolean;
}) {
  return (
    <div
      className={`shrink-0 w-5 h-5 border-2 flex items-center justify-center transition-colors ${
        multiple ? "rounded-md" : "rounded-full"
      } ${
        selected
          ? "border-indigo-500 bg-indigo-500 text-white"
          : "border-border-strong"
      }`}
    >
      {selected && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {multiple ? <path d="M20 6L9 17l-5-5" /> : <circle cx="12" cy="12" r="5" fill="currentColor" stroke="none" />}
        </svg>
      )}
    </div>
  );
}

type PollOption = Poll["options"][number];

/**
 * WHO VOTED FOR THIS OPTION — the stack that came back (OSO-447)
 *
 * A public ("ikke anonym") poll has always drawn a row of faces under each
 * bar; the port shipped `votersVisible` and `voterCount` with no voter list, so
 * the line "Alle kan se, hvem der har stemt" was the only trace left of it.
 * `PollOption.voters` is a bounded, frozen array (D9) of at most ten
 * `UserSummary`s, and `voteCount` is the true total — so "+N" is the two
 * numbers, not a second request.
 *
 * `Facepile` DRAWS IT, because the group header's member row is the same
 * idiom and was the same markup — which is also what gives both of them the
 * profile hover card (OSO-397) from one place instead of two.
 *
 * IT IS EMPTY WHEREVER THE READER MAY NOT SEE THE BALLOT, and empty because
 * the SERVER never read it — an anonymous poll, a signed-out reader, a
 * `results` not yet earned. `showVoters` below is `votersVisible`, which is
 * about the poll and not about the reader; the privacy rule is
 * `contentRepo.pollVotersVisibleTo`, at the query, so there is nothing here to
 * get wrong.
 *
 * SIX FACES, NOT THE TEN THE WIRE CARRIES. A face is 36px drawn (32px avatar
 * + the 2px ring) overlapping by 8, so ten of them plus "+N" is ~316px — and
 * an option bar inside a post card has ~285px at a 375px viewport and ~230px
 * at 320px. The avatars cannot shrink, and since the bar dropped
 * `overflow-hidden` so a name tooltip can escape it, nothing clips them
 * either: the overflow lands on the card. Six fits the narrowest phone and
 * the rest are counted in "+N", which is what "+N" is for. `GroupHeader`
 * slices to eight, in a much wider container, for the same reason.
 */
const MAX_OPTION_FACES = 6;

function PollOptionBar({
  option,
  totalVoters,
  isSelected,
  showResults,
  disabled,
  multiple,
  showVoters,
  groupId,
  onClick,
}: {
  option: PollOption;
  totalVoters: number;
  isSelected: boolean;
  showResults: boolean;
  disabled: boolean;
  multiple: boolean;
  showVoters: boolean;
  groupId?: number;
  onClick: () => void;
}) {
  const { html } = option;
  // `null` never reaches a drawn bar — `showResults` is false whenever the
  // server withheld the result (OSO-462) — but the arithmetic must not NaN.
  const votes = option.voteCount ?? 0;
  const pct = totalVoters > 0 ? Math.round((votes / totalVoters) * 100) : 0;

  if (showResults) {
    return (
      /**
       * NO `overflow-hidden`. It clipped everything the facepile drew above
       * the bar — first the name tooltip, whose centred box lost most of
       * itself against the ~28px between the leading avatar and the left edge.
       * `ProfileHoverCard` portals to `document.body` and is out of its reach
       * now, but the fill below carries its own `rounded-lg`, which is all the
       * clip was ever doing, so putting it back would only sit and wait for
       * the next thing that has to overflow.
       */
      <div
        className={`relative w-full rounded-lg border ${
          isSelected
            ? "border-indigo-400 dark:border-indigo-500"
            : "border-border"
        }`}
      >
        <div
          className={`absolute inset-y-0 left-0 transition-all duration-500 rounded-lg ${
            isSelected
              ? "bg-primary-muted/40"
              : "bg-muted/50"
          }`}
          style={{ width: `${pct}%` }}
        />
        <div className="relative flex items-center gap-3 px-3 py-2.5">
          <span
            className={`flex-1 text-sm ${isSelected ? "font-medium text-foreground" : "text-foreground"}`}
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
            {pct}%
          </span>
        </div>
        {/* Under the bar, inside the same rounded box, so a face belongs
            unmistakably to one option. Only with the results — before you have
            voted there is nothing to attribute.

            `?? []` IS ABOUT SKEW, NOT TYPES. `voters` is required on the wire,
            but a next-app build talking to a backend that predates OSO-447 —
            the normal state of stage and prod between two deploys — has none,
            and an unguarded `.length` took the whole post card down with it
            instead of degrading to no faces. */}
        {showVoters && (
          <Facepile
            people={(option.voters ?? []).slice(0, MAX_OPTION_FACES)}
            total={votes}
            groupId={groupId}
            className="px-3 pb-2"
          />
        )}
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative w-full text-left rounded-lg border transition-colors overflow-hidden ${
        isSelected
          ? "border-indigo-400 dark:border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30"
          : "border-border hover:border-gray-300 dark:hover:border-gray-600"
      } disabled:opacity-60 disabled:cursor-not-allowed`}
    >
      <div className="relative flex items-center gap-3 px-3 py-2.5">
        <OptionMarker multiple={multiple} selected={isSelected} />
        <span
          className="flex-1 text-sm text-foreground"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </button>
  );
}

/**
 * A POLL IS ROWS NOW
 *
 * `mergePollResponse` USED TO LIVE HERE — thirty lines that took a
 * `Record<string, unknown>` from `PUT /polls/vote`, reached into it field by
 * field with a cast per field, and fell back to the previous value for each
 * one because the Discourse serializer omitted `public` on secret polls and a
 * missing key must not read as "changed to secret".
 *
 * `PUT /posts/{id}/polls/{pollId}/vote` answers the COMPLETE `Poll` — every
 * option's `voteCount`, this viewer's `myVotes`, `voterCount`, `status`,
 * `votersVisible`. There is nothing to merge and nothing to guess: the response
 * replaces the poll.
 */
/**
 * The line that stands where "N stemmer" would, when the server withheld the
 * result. Keyed on `results`, which the poll still carries whatever the reader
 * may see; an unrecognised value gets the neutral line, since the server fails
 * closed on it too.
 */
function resultsWithheldLine(results: string, closeAt: string | null): string {
  switch (results) {
    case "on_close":
      // WITH THE DATE WHEN THERE IS ONE (OSO-548). "Når afstemningen lukker"
      // is not an answer to "when do I come back?", and since OSO-548 the
      // author has to pick a closing time to choose this arm at all — so a
      // poll reaching this line without one is a legacy row, and the vaguer
      // sentence is the honest thing to say about it.
      return closeAt
        ? `Resultatet vises, når afstemningen lukker ${formatShortDateTime(closeAt, THETA_TIME_ZONE)}`
        : "Resultatet vises, når afstemningen lukker";
    case "staff_only":
      return "Resultatet er kun for administratorer";
    case "on_vote":
      return "Resultatet vises, når du har stemt";
    default:
      return "Resultatet er skjult";
  }
}

export function PollDisplay({
  poll: serverPoll,
  postId,
  groupId,
}: {
  poll: Poll;
  postId: number;
  /**
   * The group the post is in. Only the voter hover card reads it, and only to
   * make the card contextual — the role badge, "medlem siden" and the
   * affiliation chip are all about a group. Optional, because a card with no
   * group still renders; it just says less.
   */
  groupId?: number;
}) {
  const [localPoll, setLocalPoll] = useState<Poll | null>(null);
  const poll = localPoll ?? serverPoll;

  // Non-null only while a multiple-choice ballot is being filled in — either a
  // poll the user has not voted on yet, or one reopened via "Skift svar".
  const [draft, setDraft] = useState<string[] | null>(null);

  const userVotes = poll.myVotes;
  const isMultiple = poll.type === "multiple";
  const hasVoted = userVotes.length > 0;
  const isClosed = poll.status === "closed";
  // `can.vote` is the server's own predicate for this route — a closed poll, a
  // viewer who is not a member, a poll that has already taken this ballot.
  const canVote = poll.can.vote;
  /**
   * THE RESULT MAY BE WITHHELD (OSO-462). `voterCount` and every
   * `voteCount` are `null` when `results` says this reader may not see them
   * yet — `on_close` before the poll closes, `staff_only` without staff,
   * `on_vote` before you have voted. The server decides; this component only
   * has to not draw bars at 0% from a number that is not there. The ballot is
   * still "answered" (no more clicking, "Fjern stemme" still offered) — it is
   * the RESULT that is replaced by a line saying when it will show.
   */
  const resultsWithheld = poll.voterCount == null;
  const ballotAnswered = isClosed || (hasVoted && draft === null);
  const showResults = ballotAnswered && !resultsWithheld;
  const selection = draft ?? userVotes;
  const selectedOptions = new Set(showResults ? userVotes : selection);

  const voteMutation = useMutation({
    mutationFn: (digests: string[]) => votePoll(postId, poll.id, digests),
    onSuccess: (updated) => {
      setLocalPoll(updated);
      setDraft(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => removePollVote(postId, poll.id),
    onSuccess: (updated) => {
      setLocalPoll(updated);
      setDraft(null);
    },
  });

  const isPending = voteMutation.isPending || removeMutation.isPending;

  const handleOptionClick = (digest: string) => {
    if (isClosed || isPending || !canVote) return;

    // Single-choice polls vote on click, as before.
    if (!isMultiple) {
      voteMutation.mutate([digest]);
      return;
    }

    const next = selection.includes(digest)
      ? selection.filter((d) => d !== digest)
      : [...selection, digest];
    // An explicit max= would be rejected server-side, so don't let the user
    // build a ballot that cannot be submitted.
    if (poll.max != null && next.length > poll.max) return;
    setDraft(next);
  };

  const minChoices = poll.min ?? 1;

  return (
    <div className="mx-4 mb-3 rounded-xl border border-border bg-gray-50/50 dark:bg-gray-800/30 p-4 space-y-3">
      <h4 className="text-sm font-semibold text-foreground">
        {poll.name}
      </h4>

      <div className="space-y-1.5">
        {poll.options.map((opt) => (
          <PollOptionBar
            key={opt.digest}
            option={opt}
            totalVoters={poll.voterCount ?? 0}
            isSelected={selectedOptions.has(opt.digest)}
            showResults={showResults}
            disabled={isClosed || isPending || !canVote || (ballotAnswered && resultsWithheld)}
            multiple={isMultiple}
            showVoters={poll.votersVisible}
            groupId={groupId}
            onClick={() => handleOptionClick(opt.digest)}
          />
        ))}
      </div>

      {isMultiple && !ballotAnswered && (
        <Button
          fullWidth
          onClick={() => voteMutation.mutate(selection)}
          disabled={selection.length < minChoices || isPending || !canVote}
        >
          Stem
        </Button>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {poll.voterCount == null ? (
          <span>{resultsWithheldLine(poll.results, poll.closeAt)}</span>
        ) : (
          <span>
            {poll.voterCount} {poll.voterCount === 1 ? "stemme" : "stemmer"}
          </span>
        )}
        {isMultiple && (
          <>
            <span>·</span>
            <span>Vælg flere</span>
          </>
        )}
        {poll.votersVisible && (
          <>
            <span>·</span>
            <span>Alle kan se, hvem der har stemt</span>
          </>
        )}
        {isPending && (
          <>
            <span>·</span>
            <span className="text-indigo-500">Opdaterer...</span>
          </>
        )}
        {isClosed && (
          <>
            <span>·</span>
            <span>Afstemningen er lukket</span>
          </>
        )}
        {/* WHEN IT WILL CLOSE — only while it is still open, and not when the
            withheld line above already names the date, which only its
            `on_close` arm does. `isClosed` is the SERVER's answer (`status`
            is derived from `closeAt` there, OSO-548), so this never
            contradicts the line beside it. */}
        {!isClosed &&
          poll.closeAt &&
          !(poll.results === "on_close" && poll.voterCount == null) && (
          <>
            <span>·</span>
            {/* THE ZONE IS STATED, and both close-time strings in this file
                state it. A poll renders on the server now (OSO-673 seeds
                `PostCard` from a server read), so a bare `formatShortDateTime`
                would format the machine's zone into the HTML and the reader's
                into the hydrated tree — a poll closing 00.30 Danish time reads
                "12. september 22.30" on the previous day until React quietly
                replaces it. Unlike `TimeAgo`, which is approximate and merely
                suppresses the warning, this is an exact time somebody plans
                around, so it is pinned rather than suppressed. The cost, named
                so it is a choice: a reader abroad sees Danish time, which is
                the same trade the event page already takes. */}
            <span>Lukker {formatShortDateTime(poll.closeAt, THETA_TIME_ZONE)}</span>
          </>
        )}
        {isMultiple && hasVoted && !isClosed && !isPending && draft === null && canVote && (
          <>
            <span>·</span>
            <button
              onClick={() => setDraft(userVotes)}
              className="text-primary hover:underline"
            >
              Skift svar
            </button>
          </>
        )}
        {hasVoted && !isClosed && !isPending && canVote && (
          <>
            <span>·</span>
            <button
              onClick={() => removeMutation.mutate()}
              className="text-primary hover:underline"
            >
              Fjern stemme
            </button>
          </>
        )}
      </div>
    </div>
  );
}
